import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import userViewGuard from "~/hooks/useViewPermssionsCheck";
import { useInvoice } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import InvoiceDetailsView from "~/modules/billing/components/InvoiceDetailsView";
import type {
	ValidInvoiceDetailsNavigatedFromOrigin,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import {
	validInvoiceDetailsNavigatedFromOrigins,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import { useQuota } from "~/modules/client/api/quota/quotaQueries.ts";
import type { QuotaWithRelations } from "~/modules/client/api/quota/quotaTypes.ts";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";
import LoadingPage from "~/pages/LoadingPage.tsx";
import { PermissionNames } from "~/types/entityNames.ts";


const InvoiceDetailsPage: FunctionComponent = () => {
	const guard = userViewGuard([PermissionNames.CanManageInvoices, PermissionNames.CanViewInvoices, PermissionNames.CanManageProjects]);
	if (guard) return guard;

	const [searchParams] = useSearchParams();
	const fromParam = searchParams.get("from") as ValidInvoiceDetailsNavigatedFromOrigin;
	const { invoiceId } = useParams();
	// preload all users
	useAllUsers();
	const {
		data: invoiceData,
		isLoading: invoiceIsLoading,
	} = useInvoice(invoiceId!);

	const quota = invoiceData ? invoiceData.quota : undefined;

	const {
		data: quotaDataWithRelations,
		isLoading: quotaIsLoading,
	} = useQuota(quota?.clientId || "", quota?.id || "", true);

	const navigatedFromSanitised = useMemo(() => {
		if (fromParam) {
			if (Object.values(validInvoiceDetailsNavigatedFromOrigins).includes(fromParam)) {
				return fromParam;
			}
		}
		return undefined;
	}, [fromParam]);

	const isLoading = invoiceIsLoading || quotaIsLoading;
	if (isLoading) return <LoadingPage />;

	if (invoiceData === undefined || !!quota && !quotaDataWithRelations) {
		return null;
	}


	return <InvoiceDetailsView invoiceData={invoiceData}
							   navigatedFrom={navigatedFromSanitised}
							   quota={quotaDataWithRelations as QuotaWithRelations | undefined}
	/>;
};

export default InvoiceDetailsPage;